import React from 'react';
import Box from '@mui/material/Box';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { UnitSystem } from 'providers/api';
import { i18n } from 'i18n';
import { distanceMetricToImperial } from 'utils/unitConversions';
import PCSpool from '../../../MineModel/Spools/PCSpool';
import { Model } from '../../../hooks/useModelState';
import { TOOLBAR_MENU_Z_INDEX } from './utils';

type DisplayUnit = 'metric' | 'imperial';

interface PipeLengthTotalsPopoutProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  model: Model;
}

interface PipeTotal {
  pipeId: string;
  pipeName: string;
  pipeColour: string;
  totalMetres: number;
}

const formatLength = (metres: number, unit: DisplayUnit): { value: string; suffix: string } => {
  const value = unit === 'imperial' ? distanceMetricToImperial(metres) : metres;
  return { value: value.toFixed(0), suffix: unit === 'imperial' ? 'ft' : 'm' };
};

const computeTotals = (model: Model): PipeTotal[] => {
  const grouped = Object.values(model.spools).reduce<Record<string, PipeTotal>>(
    (acc, spool: PCSpool) => {
      const pipeId = spool.pipe?.pipeId ?? 'unknown';
      const pipeName = spool.pipe?.name ?? 'Unknown';
      const pipeColour = spool.pipe?.colour ?? '#888888';
      const existing = acc[pipeId];
      const totalMetres = (existing?.totalMetres ?? 0) + spool.length();
      return { ...acc, [pipeId]: { pipeId, pipeName, pipeColour, totalMetres } };
    },
    {},
  );
  return Object.values(grouped);
};

const PipeLengthTotalsPopout = ({
  open,
  anchorEl,
  onClose,
  model,
}: PipeLengthTotalsPopoutProps) => {
  const totals = React.useMemo(() => computeTotals(model), [model]);
  const displayUnit: DisplayUnit = model.project?.unitSystemPreference === UnitSystem.Imperial ? 'imperial' : 'metric';

  const handleCopyRow = (pipeName: string, metres: number) => {
    const { value, suffix } = formatLength(metres, displayUnit);
    navigator.clipboard.writeText(`${pipeName}: ${value} ${suffix}`);
  };

  const handleCopyAll = () => {
    const lines = totals.map(({ pipeName, totalMetres }) => {
      const { value, suffix } = formatLength(totalMetres, displayUnit);
      return `${pipeName}: ${value} ${suffix}`;
    });
    navigator.clipboard.writeText(lines.join('\n'));
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      sx={{ zIndex: TOOLBAR_MENU_Z_INDEX }}
    >
      <Box sx={{ p: 1.5, minWidth: 260, maxWidth: 360 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle2">{i18n._('Pipe Length Totals')}</Typography>
          {totals.length > 0 && (
            <Tooltip title={i18n._('Copy to clipboard')}>
              <Button
                size="small"
                color="secondary"
                startIcon={<ContentCopyIcon fontSize="small" />}
                onClick={handleCopyAll}
                sx={{ textTransform: 'none' }}
              >
                {i18n._('Copy all')}
              </Button>
            </Tooltip>
          )}
        </Box>
        <Divider />
        {totals.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {i18n._('No spools in model')}
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', mt: 0.5 }}>
            {totals.map(({ pipeId, pipeName, pipeColour, totalMetres }) => {
              const { value, suffix } = formatLength(totalMetres, displayUnit);
              return (
                <Box
                  key={pipeId}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    py: 0.5,
                  }}
                >
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '2px',
                      backgroundColor: pipeColour,
                      border: '1px solid',
                      borderColor: 'divider',
                      flexShrink: 0,
                    }}
                  />
                  <Typography variant="body2" sx={{ flexGrow: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {pipeName}
                  </Typography>
                  <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {`${value} ${suffix}`}
                  </Typography>
                  <Tooltip title={i18n._('Copy to clipboard')}>
                    <IconButton size="small" onClick={() => handleCopyRow(pipeName, totalMetres)}>
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    </Popover>
  );
};

export default PipeLengthTotalsPopout;
